import type { TSession } from "@/types/TSession";
import { createColumnHelper } from "@tanstack/react-table";
import { format } from "date-fns";
import { TruncatedCopyableText } from "@/components/ui/custom/truncated-copyable-text";
import { useTranslation } from "react-i18next";

const columnHelper = createColumnHelper<TSession>();

export function getStatusColor(status: string) {
  const isActive = status === "active";
  const isClosed = status === "closed";

  let colorClass = "bg-yellow-100 text-yellow-800";
  if (isActive) {
    colorClass = "bg-blue-100 text-blue-800";
  } else if (isClosed) {
    colorClass = "bg-green-100 text-green-800";
  }
  return colorClass;
}

export function getSessionColumns(
  handleStatusClick: (session: TSession) => void,
  isLoading: string | null
) {
  const { t } = useTranslation();  return [
    columnHelper.accessor("session_id", {
      header: t("sessions.id"),
      cell: (info) => (
        <TruncatedCopyableText text={info.getValue()} />
      ),
      size: 100,
    }),
    columnHelper.accessor("status", {
      header: t("sessions.status"),
      cell: (info) => {
        const session = info.row.original;
        const status = info.getValue();
        const sessionId = session.session_id;
        const capitalizedStatus = status.charAt(0).toUpperCase() + status.slice(1);
        const isClosed = status === "closed";
        const isLoadingThis = isLoading === sessionId;

        const colorClass = getStatusColor(status);

        return (
          <span
            className={`px-2 py-0.5 text-xs font-medium rounded-full ${colorClass} ${!isClosed ? "cursor-pointer hover:opacity-80" : ""} ${isLoadingThis ? "animate-pulse" : ""}`}
            onMouseUp={!isClosed ? () => handleStatusClick(session) : undefined}
            title={!isClosed ? t("sessions.closeSession") : undefined}
          >
            {isLoadingThis ? t("sessions.closingStatus") : capitalizedStatus}
          </span>
        );
      },
      size: 80,
    }),
    columnHelper.accessor("created_at", {
      header: t("sessions.created"),
      cell: (info) => {
        const value = info.getValue();
        try {
          return format(new Date(value), "MMM d, yyyy HH:mm");
        } catch {
          return value;
        }
      },
      size: 120,
    }),
    columnHelper.accessor("duration", {
      header: t("common.duration"),
      cell: (info) => {
        const seconds = info.getValue();
        if (seconds < 60) return `${seconds}s`;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
      },
      size: 80,
    }),
    columnHelper.accessor("credits_consumed", {
      header: t("sessions.credits"),
      cell: (info) => {
        const credits = info.getValue();
        return typeof credits === "number" ? credits.toFixed(2) : "0";
      },
      size: 80,
    }),
    columnHelper.accessor("mode", {
      header: t("sessions.mode"),
      cell: (info) => info.getValue() || "-",
      size: 80,
    }),
  ];
}
